import React, { useMemo, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
} from "antd";
import moment from "moment";
import { displayFormat } from "../../../../lib/utils/formatDate";
import { useAppDispatch, useAppSelector } from "../../../..";
import { checkIsLoading, getSelectOptions, useActiveModal } from "../../../../lib/utils";
import { ReactComponent as LoupeIcon } from "../../../../img/loupe-icon.svg";
import { ReactComponent as UnselectIcon } from "../../../../img/close-modal-pure-icon.svg";
import { ReactComponent as GroupOfferingIcon } from "../../../../img/group-offering-icon.svg";
import { ReactComponent as OfferingIcon } from "../../../../img/offering-icon.svg";
import type { ISearchFilterField, Offer } from "../../../../lib/types";
import { BulkFiltersToggler } from "../../../../components/BulkFiltersToggler/BulkFiltersToggler";
import { IParameterSearchType } from "../../context/PoliciesContext/types";
import { useOfferingGroupsContext } from "../../context/OfferingGroupsContext/OfferingGroupsContext";
import { offeringGroupsActions } from "../../store/slice/offeringGroupsSlice";
import "./OfferingGroupsListSearch.less";
import { OfferListModal } from "../../../../components/OfferList/OfferListModal/OfferListModal";
import { useOfferList } from "../../../../components/OfferList/utils/useOfferList";
import { OfferingGroupsSearchModal } from "../OfferingGroupsSearchModal/OfferingGroupsSearchModal";
import { IOfferingGroup } from "../../types/IOfferingGroup";

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const OfferingGroupsListSearch = () => {
  const {
    isSearchOpen,
    toggleSearchOpen,
    handleOfferingGroupsListSearch,
    offeringGroupsListSearchForm,
  } = useOfferingGroupsContext();
  const {
    offeringGroupsLoading,
    offeringGroupsTypesDic,
    offeringGroupsTypesDicLoading,
  } = useAppSelector((state) => state.offeringGroups);

  const dispatch = useAppDispatch();

  const offerListProps = useOfferList({
    initialOfferListViewMode: "selectOffer",
    initialSearchValues: { onDate: moment(), page: 0, size: 10 },
  });

  const { activeModal, setActiveModal } = useActiveModal<"" | "offering" | "offering_group">("");

  const closeModal = () => {
    setActiveModal("");
  };

  const submitOfferListModal = (offer: Offer) => {
    offeringGroupsListSearchForm.setFieldsValue({
      memberObjectId: offer.id,
      memberObjectName: offer.extName,
      memberTypeAlias: "offering",
    });
    closeModal();
  };

  const submitOfferingGroupsSearchModal = (offeringGroup: IOfferingGroup) => {
    offeringGroupsListSearchForm.setFieldsValue({
      memberObjectId: offeringGroup.id,
      memberObjectName: offeringGroup.name,
      memberTypeAlias: "offering_group",
    });
    closeModal();
  };

  const unselect = () => {
    offeringGroupsListSearchForm.resetFields([
      "memberObjectId",
      "memberObjectName",
      "memberTypeAlias",
    ]);
  };

  const [parameterSearchType, setParameterSearchType] = useState<IParameterSearchType>(
    "offering_group"
  );

  const toggleParameterSearchType = () => {
    offeringGroupsListSearchForm.setFieldsValue({
      memberObjectId: undefined,
      memberObjectName: undefined,
      memberTypeAlias: undefined,
    });
    setParameterSearchType(parameterSearchType === "offering" ? "offering_group" : "offering");
  };

  const allFields: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Наименование",
        fieldElement: (
          <Form.Item label="Наименование" name="name">
            <Input placeholder="Введите текст..." allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Элемент группы",
        fieldElement: (
          <Form.Item
            className="form-item-required__hidden-error__mb0 offering-groups-list-search__parameterOne"
            label={<div>Элемент группы:</div>}
            shouldUpdate
          >
            {({ getFieldValue }) => {
              const memberObjectName = getFieldValue("memberObjectName");
              const unselectTitle =
                parameterSearchType === "offering" ? "Сбросить предложение" : "Сбросить группу";
              return (
                <Row>
                  <Col span={21}>
                    <Form.Item name="memberObjectName">
                      <Input
                        allowClear
                        placeholder={
                          parameterSearchType === "offering"
                            ? "Выберите предложение"
                            : "Выберите группу предложений"
                        }
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Row wrap={false}>
                      {parameterSearchType === "offering" && (
                        <OfferingIcon
                          className="offering-groups-list-search__offerIcon"
                          onClick={toggleParameterSearchType}
                        />
                      )}
                      {parameterSearchType === "offering_group" && (
                        <GroupOfferingIcon
                          className="offering-groups-list-search__offerIcon"
                          onClick={toggleParameterSearchType}
                        />
                      )}
                      {!memberObjectName && (
                        <LoupeIcon
                          className="offering-groups-list-search__loupeIcon"
                          onClick={() => {
                            setActiveModal(parameterSearchType);
                          }}
                        />
                      )}
                      {memberObjectName && (
                        <Tooltip title={unselectTitle}>
                          <UnselectIcon
                            className="offering-groups-list-search__unselectIcon"
                            onClick={unselect}
                          />
                        </Tooltip>
                      )}
                    </Row>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Показать на дату",
        fieldElement: (
          <Form.Item label="Показать на дату" name="onDate">
            <DatePicker format={displayFormat} allowClear={false} />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Тип",
        fieldElement: (
          <Form.Item label="Тип" name="groupingTypeId">
            <Select
              options={getSelectOptions({
                list: offeringGroupsTypesDic,
              })}
              loading={checkIsLoading(offeringGroupsTypesDicLoading)}
              placeholder="Выбрать"
              allowClear
            />
          </Form.Item>
        ),
        visible: true,
      },
    ],
    [offeringGroupsTypesDic, parameterSearchType]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>(allFields);

  return (
    <div className="offering-groups-list-search">
      <Collapse
        expandIconPosition="right"
        activeKey={isSearchOpen ? "1" : ""}
        onChange={toggleSearchOpen}
      >
        <Collapse.Panel header="Расширенный поиск" key="1">
          <Row justify="end">
            <BulkFiltersToggler
              allFields={allFields}
              setFields={setFields}
              filterName="offering-groups-list"
              minSave={["Показать на дату"]}
            />
          </Row>
          <Form
            form={offeringGroupsListSearchForm}
            onFinish={handleOfferingGroupsListSearch}
            wrapperCol={{ span: 17 }}
            labelCol={{ span: 7 }}
            labelAlign="left"
            initialValues={{ onDate: moment() }}
            onReset={() => {
              offeringGroupsListSearchForm.resetFields();
              dispatch(offeringGroupsActions.cleanOfferingGroups());
            }}
          >
            <Row gutter={50}>
              {fields
                .filter(({ visible }) => visible)
                .map(({ fieldElement }, idx) => (
                  <Col span={8} key={idx}>
                    {fieldElement}
                  </Col>
                ))}
            </Row>
            <Row justify="end">
              <Space size={20}>
                <Button
                  htmlType="reset"
                  onClick={() => {
                    setParameterSearchType("offering_group");
                  }}
                >
                  Очистить
                </Button>
                <Spin spinning={checkIsLoading(offeringGroupsLoading)}>
                  <Button type="primary" htmlType="submit">
                    Применить
                  </Button>
                </Spin>
              </Space>
            </Row>
          </Form>
        </Collapse.Panel>
      </Collapse>
      <OfferListModal
        visible={parameterSearchType === "offering" && activeModal === "offering"}
        onOk={submitOfferListModal}
        onCancel={closeModal}
        offerListProps={{ ...offerListProps, filterName: "group-offer-2" }}
      />
      <OfferingGroupsSearchModal
        visible={parameterSearchType === "offering_group" && activeModal === "offering_group"}
        onCancel={closeModal}
        onSubmit={submitOfferingGroupsSearchModal}
      />
    </div>
  );
};
